/**
 * ẢNH CỦA MÀN — trả về đường dẫn ảnh để sảnh chọn màn hiển thị.
 *
 * ⚠️ ĐƯỜNG NÂNG CẤP LÊN ẢNH AI (người dùng: "sinh tạm ra SVG sau này nâng cấp thành image
 * sinh từ AI sau") — đã chừa sẵn, KHÔNG phải sửa UI:
 *
 *   1. Sinh/ đặt ảnh AI vào `art/ai/map-<id>.png` (VD `art/ai/map-1.2.png`).
 *   2. Thêm `map-*.png` vào danh sách trắng của `tools/sync-art.mjs` (đã có sẵn).
 *   3. Chạy `npm run sync:art` — nén sang WebP ở `public/art/ai/`.
 *
 * Hàm dưới đây tự dò: có `art/ai/map-<id>.png` thì dùng ảnh AI, không thì rơi về SVG.
 * ⚠️ Đuôi file runtime do `artUrl()` quyết định (`.webp` sau khi nén), KHÔNG phải `.png`
 * cứng — xem `src/art/art-url.ts`.
 * Nhờ vậy nâng cấp từng màn một cũng được — màn nào chưa có ảnh AI vẫn có SVG.
 */

import { artUrl } from '../art/art-url';

/** Ảnh SVG tạm (sinh bằng `node tools/gen-map-art.mjs`). */
export function mapArtSvg(mapId: string): string {
  return `art/maps/map-${mapId}.svg`;
}

/**
 * Ảnh AI của màn NẾU có — kiểm bằng 1 request HEAD, có nhớ kết quả để không hỏi lại.
 *
 * Vì sao phải dò động: lúc build không biết người dùng đã sinh ảnh AI cho màn nào chưa
 * (hạn ngạch AI có thể hết giữa chừng ⇒ chỉ có một phần ảnh). Dò 1 lần cho mỗi màn là đủ.
 */
const aiCache = new Map<string, boolean>();

export async function probeMapArtAi(mapId: string): Promise<boolean> {
  const hit = aiCache.get(mapId);
  if (hit !== undefined) return hit;
  let ok = false;
  try {
    const res = await fetch(artUrl(`art/ai/map-${mapId}.png`), { method: 'HEAD' });
    /*
     * ⚠️ BẪY ĐÃ DÍNH: KHÔNG được chỉ kiểm `res.ok`.
     * Vite (dev) và preview đều trả **200 kèm HTML** cho đường dẫn tĩnh KHÔNG tồn tại
     * (fallback SPA) ⇒ `res.ok === true` với ảnh chưa hề có, và sảnh chọn màn trỏ vào
     * `art/ai/map-1.1.png` không tồn tại ⇒ ô ảnh TRỐNG (đo được: 0/15 ảnh nạp được).
     * Phải kiểm CONTENT-TYPE là ảnh.
     */
    const type = res.headers.get('content-type') ?? '';
    ok = res.ok && type.startsWith('image/');
  } catch {
    ok = false;
  }
  aiCache.set(mapId, ok);
  return ok;
}

/**
 * Ảnh dùng được NGAY (không cần await): SVG. Dùng cho lần vẽ đầu tiên để sảnh không trống,
 * sau đó `probeMapArtAi()` nâng cấp dần sang PNG nếu có.
 */
export function mapArtSrc(mapId: string): string {
  return aiCache.get(mapId) ? artUrl(`art/ai/map-${mapId}.png`) : mapArtSvg(mapId);
}

/** Nâng cấp sang ảnh AI nếu có (gọi 1 lần cho mỗi màn khi mở sảnh). */
export async function upgradeMapArt(mapId: string): Promise<string | null> {
  if (aiCache.get(mapId)) return null; // đã là ảnh AI rồi
  const has = await probeMapArtAi(mapId);
  return has ? artUrl(`art/ai/map-${mapId}.png`) : null;
}
